import React, { useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Phone, User, MessageSquare, Send, Github, Twitter, Linkedin } from 'lucide-react';
export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`New project inquiry from ${formData.name}`);
    const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone || 'Not provided'}\n\nMessage:\n${formData.message}`);
    window.location.href = `mailto:hello@taptheory.be?subject=${subject}&body=${body}`;
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <div className="min-h-screen bg-background font-sans text-text-main flex flex-col" data-id="element-184">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-white/5" data-id="element-185">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-186">
          <div className="flex justify-between items-center h-20" data-id="element-187">
            <Link to="/" className="font-mono text-xl font-bold tracking-tighter text-white" data-id="element-188">
              taptheory<span className="text-primary" data-id="element-189">.be</span>
            </Link>

            <Link to="/" className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors" data-id="element-190">
              <ArrowLeft className="w-4 h-4" data-id="element-191" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <section className="flex-1 pt-32 pb-24 relative overflow-hidden flex items-center" data-id="element-192">
        {/* Background */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/8 via-background to-background pointer-events-none" data-id="element-193"></div>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-194"></div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-195">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="mb-12" data-id="element-196">
            <span className="font-mono text-sm font-bold text-primary tracking-widest uppercase block mb-3" data-id="element-197">
              Contact
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-4" data-id="element-198">
              Get in touch
            </h1>
            <p className="text-lg text-gray-400 max-w-lg" data-id="element-199">
              Have a project in mind? Tell us about it and we'll get back to you
              within 24 hours.
            </p>
          </motion.div>

          {/* Form */}
          {submitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4
        }} className="bg-white/5 backdrop-blur-xl p-12 rounded-3xl border border-white/10 shadow-2xl text-center" data-id="element-200">
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-201">
                <Send className="w-7 h-7 text-primary" data-id="element-202" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3" data-id="element-203">
                Message sent!
              </h2>
              <p className="text-gray-400 mb-8" data-id="element-204">
                Thanks for reaching out. We'll be in touch soon.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-white/10 border border-white/10 rounded-xl hover:bg-white/20 transition-all" data-id="element-205">
                <ArrowLeft className="w-4 h-4" data-id="element-206" />
                Back to Home
              </Link>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} className="bg-white/5 backdrop-blur-xl p-8 md:p-10 rounded-3xl border border-white/10 shadow-2xl" data-id="element-207">
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-208">
                {/* Name */}
                <motion.div initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.25
            }} data-id="element-209">
                  <label htmlFor="name" className="block text-xs font-mono text-primary mb-2 uppercase tracking-wider" data-id="element-210">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-211">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" data-id="element-212" />
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="John Doe" data-id="element-213" />
                  </div>
                </motion.div>

                {/* Email */}
                <motion.div initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.35
            }} data-id="element-214">
                  <label htmlFor="email" className="block text-xs font-mono text-primary mb-2 uppercase tracking-wider" data-id="element-215">
                    Email Address
                  </label>
                  <div className="relative" data-id="element-216">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" data-id="element-217" />
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="hello@company.com" data-id="element-218" />
                  </div>
                </motion.div>

                {/* Phone */}
                <motion.div initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.45
            }} data-id="element-219">
                  <label htmlFor="phone" className="block text-xs font-mono text-primary mb-2 uppercase tracking-wider" data-id="element-220">
                    Phone <span className="text-gray-600" data-id="element-221">(optional)</span>
                  </label>
                  <div className="relative" data-id="element-222">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" data-id="element-223" />
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="+32 123 456 789" data-id="element-224" />
                  </div>
                </motion.div>

                {/* Message */}
                <motion.div initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.55
            }} data-id="element-225">
                  <label htmlFor="message" className="block text-xs font-mono text-primary mb-2 uppercase tracking-wider" data-id="element-226">
                    Message
                  </label>
                  <div className="relative" data-id="element-227">
                    <MessageSquare className="absolute left-4 top-4 w-4 h-4 text-gray-600" data-id="element-228" />
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all resize-none" placeholder="Tell us about your project..." data-id="element-229"></textarea>
                  </div>
                </motion.div>

                {/* Submit */}
                <motion.button initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.65
            }} type="submit" className="group w-full bg-primary hover:bg-primary-dark text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-230">
                  Send Message
                  <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-231" />
                </motion.button>
              </form>

              <div className="mt-8 pt-6 border-t border-white/5 text-center" data-id="element-232">
                <p className="text-xs text-gray-500 font-mono" data-id="element-233">
                  Or email us directly at{' '}
                  <a href="mailto:hello@taptheory.be" className="text-primary hover:underline" data-id="element-234">
                    hello@taptheory.be
                  </a>
                </p>
              </div>
            </motion.div>}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 bg-black" data-id="element-235">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-236">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-237">
            <span className="font-mono text-xl font-bold tracking-tighter text-white" data-id="element-238">
              taptheory<span className="text-primary" data-id="element-239">.be</span>
            </span>

            <div className="flex space-x-8 text-sm font-medium text-gray-400" data-id="element-240">
              <Link to="/contact" className="text-primary transition-colors" data-id="element-241">
                Contact
              </Link>
              <Link to="/privacy" className="hover:text-primary transition-colors" data-id="element-242">
                Privacy
              </Link>
              <Link to="/terms" className="hover:text-primary transition-colors" data-id="element-243">
                Terms
              </Link>
            </div>

            <div className="flex space-x-6" data-id="element-244">
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-245">
                <Github className="w-5 h-5" data-id="element-246" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-247">
                <Twitter className="w-5 h-5" data-id="element-248" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-249">
                <Linkedin className="w-5 h-5" data-id="element-250" />
              </a>
              <a href="#" className="text-gray-500 hover:text-white transition-colors" data-id="element-251">
                <Mail className="w-5 h-5" data-id="element-252" />
              </a>
            </div>
          </div>
          <div className="mt-8 text-center text-xs text-gray-600 font-mono" data-id="element-253">
            &copy; {new Date().getFullYear()} Tap Theory. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}